import { useRef, type ComponentType } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { withIcon } from '../../../../core/components/Icon.tsx';
import { faGripDotsVertical, faTrash } from '@fortawesome/pro-regular-svg-icons';
import classnames from '../../../../core/utils/classnames.tsx';
import { toHumanReadable } from '../../../../core/utils/string.ts';
import { useOutputMapperEditor } from '../OutputMapperEditorProvider.tsx';
import ExtractSourceBlock from '../actionTypes/ExtractSourceBlock.tsx';
import AssignLiteralBlock from '../actionTypes/AssignLiteralBlock.tsx';
import TransformBlock from '../actionTypes/TransformBlock.tsx';
import FunctionCallBlock from '../actionTypes/FunctionCallBlock.tsx';
import ConditionalBlock from '../actionTypes/ConditionalBlock.tsx';
import MapArrayBlock from '../actionTypes/MapArrayBlock.tsx';
import type { Action, ActionType, AliasContextEntry } from '../types.ts';

const DragIcon = withIcon(faGripDotsVertical);
const TrashIcon = withIcon(faTrash);

const DRAG_TYPE = 'ACTION_BLOCK';

const ACTION_COMPONENTS: Record<ActionType, ComponentType<any>> = {
    extract_source: ExtractSourceBlock,
    assign_literal: AssignLiteralBlock,
    transform: TransformBlock,
    function_call: FunctionCallBlock,
    conditional: ConditionalBlock,
    map_array: MapArrayBlock,
};

const BADGE_COLORS: Record<string, string> = {
    extract_source: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
    assign_literal: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
    transform: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
    function_call: 'bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-300',
    conditional: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300',
    map_array: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300',
};

interface ActionBlockProps {
    action: Action;
    index: number;
    mappingIndex: number;
    actionId: string;
    depth?: number;
    aliasContext?: AliasContextEntry[];
    onUpdateAction?: (index: number, updates: Partial<Action>) => void;
    onDeleteAction?: (index: number) => void;
    onMoveAction?: (fromIndex: number, toIndex: number) => void;
}

const ActionBlock = ({
    action,
    index,
    mappingIndex,
    actionId,
    depth = 0,
    aliasContext = [],
    onUpdateAction,
    onDeleteAction,
    onMoveAction,
}: ActionBlockProps) => {
    const { deleteAction, moveAction } = useOutputMapperEditor();

    const handleDelete = () => {
        onDeleteAction ? onDeleteAction(index) : deleteAction(mappingIndex, index);
    };

    const dragRef = useRef<HTMLDivElement>(null);
    const previewRef = useRef<HTMLDivElement>(null);

    const dragType = onMoveAction
        ? `${DRAG_TYPE}_nested_${actionId.split('-').slice(0, -1).join('-')}`
        : `${DRAG_TYPE}_${mappingIndex}`;

    const [{ isDragging }, drag, dragPreview] = useDrag({
        type: dragType,
        item: { index, mappingIndex },
        collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    });

    const [, drop] = useDrop({
        accept: dragType,
        hover(item: { index: number; mappingIndex: number }, monitor) {
            if (!previewRef.current) return;
            const dragIndex = item.index;
            const hoverIndex = index;
            if (dragIndex === hoverIndex) return;

            const rect = previewRef.current.getBoundingClientRect();
            const middleY = (rect.bottom - rect.top) / 2;
            const clientOffset = monitor.getClientOffset();
            if (!clientOffset) return;
            const hoverClientY = clientOffset.y - rect.top;

            if (dragIndex < hoverIndex && hoverClientY < middleY) return;
            if (dragIndex > hoverIndex && hoverClientY > middleY) return;

            onMoveAction ? onMoveAction(dragIndex, hoverIndex) : moveAction(mappingIndex, dragIndex, hoverIndex);
            item.index = hoverIndex;
        },
    });

    drag(dragRef);
    drop(dragPreview(previewRef));

    if (!action) return null;

    const Component = ACTION_COMPONENTS[action.action_type];
    const onUpdate = onUpdateAction ? (updates: Partial<Action>) => onUpdateAction(index, updates) : undefined;

    return (
        <div
            ref={previewRef}
            className={classnames(
                'border border-line-weak rounded-lg bg-surface-weak transition-all',
                isDragging && 'opacity-50',
                depth > 0 && 'ml-4'
            )}
        >
            <div className="flex items-center gap-2 px-3 py-2 border-b border-line-weak bg-surface-medium rounded-t-lg">
                <div
                    ref={dragRef}
                    className="cursor-grab text-content-weak hover:text-content-strong transition-colors"
                >
                    <DragIcon className="w-4 h-4" />
                </div>
                <span
                    className={classnames(
                        'px-2 py-0.5 rounded text-xs font-medium',
                        BADGE_COLORS[action.action_type] || 'bg-gray-100 text-gray-800'
                    )}
                >
                    {toHumanReadable(action.action_type)}
                </span>
                <button
                    type="button"
                    onClick={handleDelete}
                    className="ml-auto text-content-weak hover:text-status-danger-medium transition-colors"
                >
                    <TrashIcon className="w-4 h-4" />
                </button>
            </div>
            <div className="p-3">
                {Component ? (
                    <Component
                        action={action}
                        mappingIndex={mappingIndex}
                        actionIndex={index}
                        aliasContext={aliasContext}
                        onUpdate={onUpdate}
                    />
                ) : (
                    <div className="text-sm text-content-weak">Unknown action type</div>
                )}
            </div>
        </div>
    );
};

export default ActionBlock;
